import { useState } from 'react';

interface UseCollapseAllPanelsResult {
  activeKeys: string[];
  setActiveKeys: React.Dispatch<React.SetStateAction<string[]>>;
  areAllPanelsExpanded: boolean;
  shouldShowCollapseAll: boolean;
  isPanelActive: (key: string) => boolean;
  togglePanel: (key: string) => void;
  toggleAll: () => void;
}

// Shared expand/collapse-all state machine for antd Collapse panels, used by
// both InspectionAssetsPanel and InspectionDamagePanel. Callers own their own
// initial-active-keys logic (it differs per panel) and pass the current set
// of panel keys in so toggleAll knows what "expand everything" means.
export const useCollapseAllPanels = (
  panelKeys: string[],
  isMobileInspection: boolean,
  initialActiveKeys: string[] = ['1']
): UseCollapseAllPanelsResult => {
  const [activeKeys, setActiveKeys] = useState<string[]>(initialActiveKeys);

  const areAllPanelsExpanded = activeKeys.length === panelKeys.length;
  const shouldShowCollapseAll = isMobileInspection ? activeKeys.length > 0 : areAllPanelsExpanded;

  const isPanelActive = (key: string) => activeKeys.includes(key);

  const togglePanel = (key: string) => {
    setActiveKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
  };

  const toggleAll = () => {
    setActiveKeys(prev => {
      const collapseAll = isMobileInspection ? prev.length > 0 : prev.length === panelKeys.length;
      return collapseAll ? [] : panelKeys;
    });
  };

  return {
    activeKeys,
    setActiveKeys,
    areAllPanelsExpanded,
    shouldShowCollapseAll,
    isPanelActive,
    togglePanel,
    toggleAll,
  };
};
